import { alpha } from '@mui/material/styles';
import { mobileAppGlassCard } from '../components/layout/mobile/mobileAppTheme';
import { THEME_COLORS } from '../shared/constants/theme';
import { getDashboardPageShellSx } from './dashboardSurfaceSx';
import { isMobileShellLayout } from './mobileShellLayout';
import { isTabletFormFactor } from './mobileView';

export const WALK_IN_ACCENT = '#4ce1b6';

export function isWalkInMobileLayout() {
  return isMobileShellLayout();
}

/**
 * Web tablet layouts outside the mobile app shell (flowing room picker).
 * Native tablets always use the app shell / mobile walk-in layout.
 */
export function isWalkInTabletLayout() {
  return isTabletFormFactor() && !isMobileShellLayout();
}

export function isWalkInFlowLayout() {
  return isWalkInMobileLayout() || isWalkInTabletLayout();
}

function findMobileScrollParent(target) {
  let node = target?.parentElement;
  while (node && node !== document.body) {
    const style = window.getComputedStyle(node);
    const canScroll = /(auto|scroll)/.test(`${style.overflowY}${style.overflow}`);
    if (canScroll && node.scrollHeight > node.clientHeight) return node;
    node = node.parentElement;
  }
  const main = target?.closest?.('main');
  return main instanceof HTMLElement ? main : null;
}

function getMobileHeaderBottom() {
  const header = document.querySelector('.mobile-app-shell header');
  if (!(header instanceof HTMLElement)) return 0;
  return Math.max(0, Math.round(header.getBoundingClientRect().bottom));
}

export function scrollMobileStepTopIntoView(target) {
  if (!(target instanceof HTMLElement) || typeof window === 'undefined') return;
  const scrollParent = findMobileScrollParent(target);
  if (!(scrollParent instanceof HTMLElement)) return;

  const scrollParentRect = scrollParent.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  const visibleTop = Math.max(scrollParentRect.top, getMobileHeaderBottom()) + 16;
  const delta = targetRect.top - visibleTop;

  if (Math.abs(delta) > 2) {
    scrollParent.scrollTop += delta;
  }

  // Keep iOS from moving the layout viewport while the app shell scrolls internally.
  if (window.scrollX !== 0 || window.scrollY !== 0) {
    window.scrollTo(0, 0);
  }
}

export function getWalkInShellSx(themeMode) {
  return getDashboardPageShellSx(themeMode);
}

export function getWalkInOuterPaperSx(isDark, isMobile = isWalkInMobileLayout()) {
  if (isMobile) {
    return {
      p: 0,
      bgcolor: 'transparent',
      background: 'none',
      border: 'none',
      borderRadius: 0,
      boxShadow: 'none',
    };
  }
  return {
    p: { xs: 2, sm: 3, md: 4 },
    background: isDark
      ? `linear-gradient(165deg, ${THEME_COLORS.SURFACE_ELEVATED_DARK} 0%, ${THEME_COLORS.BACKGROUND_DARK} 100%)`
      : '#ffffff',
    border: `1px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
    borderRadius: 2,
    boxShadow: isDark ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : 'none',
    transition: 'background 0.3s ease, border-color 0.3s ease',
  };
}

export function getWalkInFormPaperSx(isDark, isMobile = isWalkInMobileLayout()) {
  if (isMobile) {
    return {
      ...mobileAppGlassCard,
      p: { xs: 1.25, sm: 1.5 },
      pb: 'calc(1.25rem + env(safe-area-inset-bottom, 0px))',
      minHeight: 0,
      bgcolor: 'transparent',
    };
  }
  return {
    background: isDark
      ? `linear-gradient(165deg, ${THEME_COLORS.SURFACE_ELEVATED_DARK} 0%, ${THEME_COLORS.BACKGROUND_DARK} 100%)`
      : '#ffffff',
    border: `1px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
    borderRadius: 2,
    p: { xs: 2, sm: 3, md: 4 },
    minHeight: '600px',
    boxShadow: isDark ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : 'none',
    transition: 'background 0.3s ease, border-color 0.3s ease',
  };
}

/** Desktop walk-in rooms step: bounded height so room board scrolls internally. */
export function getWalkInRoomsStepGridItemSx() {
  return {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    minWidth: 0,
  };
}

export function getWalkInRoomsStepPaperSx(isFlowLayout = false) {
  if (isFlowLayout) {
    return {
      p: 0,
      minHeight: 0,
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      background: 'transparent',
      border: 'none',
      boxShadow: 'none',
    };
  }
  return {
    p: 0,
    minHeight: 0,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    height: 'calc(100vh - 220px)',
    maxHeight: 'calc(100vh - 220px)',
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
  };
}

export function getWalkInRoomsStepBodySx() {
  return {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };
}

export function getWalkInMobileStepBarContainerSx(accent = WALK_IN_ACCENT) {
  return {
    ...mobileAppGlassCard,
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 0.5,
    p: 0.75,
    mb: 1.5,
  };
}

export function getWalkInMobileStepItemSx(isActive, isCompleted, accent = WALK_IN_ACCENT) {
  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0.35,
    py: 0.85,
    px: 0.35,
    borderRadius: '12px',
    minHeight: 58,
    background: isActive
      ? `linear-gradient(145deg, ${alpha(accent, 0.38)} 0%, ${alpha(accent, 0.12)} 100%)`
      : isCompleted
        ? `linear-gradient(145deg, ${alpha(accent, 0.18)} 0%, ${alpha(accent, 0.06)} 100%)`
        : 'transparent',
    border: isActive
      ? `1px solid ${alpha(accent, 0.55)}`
      : isCompleted
        ? `1px solid ${alpha(accent, 0.28)}`
        : '1px solid transparent',
    boxShadow: isActive ? `0 8px 18px ${alpha(accent, 0.18)}` : 'none',
    transition: 'all 0.25s ease',
  };
}

export function getWalkInMobileStepIconSx(isActive, isCompleted, accent = WALK_IN_ACCENT) {
  return {
    width: 24,
    height: 24,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.72rem',
    fontWeight: 700,
    color: isActive || isCompleted ? '#0a0f1a' : 'rgba(148, 163, 184, 0.92)',
    backgroundColor: isActive || isCompleted ? accent : 'rgba(255,255,255,0.08)',
    '& .MuiSvgIcon-root': {
      fontSize: 16,
      color: '#0a0f1a',
    },
  };
}

export function getWalkInMobileStepLabelSx(isActive, isCompleted) {
  return {
    fontSize: '0.62rem',
    fontWeight: isActive ? 700 : 600,
    lineHeight: 1.15,
    textAlign: 'center',
    color: isActive || isCompleted ? '#f8fafc' : 'rgba(148, 163, 184, 0.88)',
    letterSpacing: '0.02em',
  };
}

export function getWalkInFooterSx(isDark, isMobile = isWalkInMobileLayout(), accent = WALK_IN_ACCENT) {
  const base = {
    display: 'flex',
    gap: isMobile ? 1 : 1.5,
    mt: isMobile ? 2.5 : 4,
    pt: isMobile ? 1.25 : 3,
    borderTop: isMobile ? 'none' : `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
  };

  if (!isMobile) return base;

  return {
    ...base,
    ...mobileAppGlassCard,
    position: 'static',
    mx: -0.25,
    mb: 0,
    pb: 'calc(10px + env(safe-area-inset-bottom, 0px))',
    borderRadius: '16px',
  };
}

export function getWalkInFooterButtonSx(variant, isDark, isMobile = isWalkInMobileLayout(), accent = WALK_IN_ACCENT) {
  const shared = {
    flex: 1,
    minHeight: isMobile ? 44 : 48,
    fontWeight: 600,
    borderRadius: isMobile ? '12px' : undefined,
  };

  if (variant === 'back') {
    return {
      ...shared,
      borderColor: isMobile ? 'rgba(255,255,255,0.12)' : isDark ? '#333246' : '#e0e0e0',
      color: isMobile ? '#e2e8f0' : isDark ? '#ddd' : 'rgb(88.3561643836, 91.0068493151, 105.1438356164)',
      '&:hover': {
        borderColor: accent,
        backgroundColor: isMobile ? alpha(accent, 0.08) : isDark ? '#38373f' : 'rgb(228.75, 234, 249.75)',
      },
      '&.Mui-disabled': {
        borderColor: isMobile ? 'rgba(255,255,255,0.06)' : isDark ? '#333246' : '#e0e0e0',
        color: isMobile ? 'rgba(148,163,184,0.45)' : isDark ? '#666' : '#999',
      },
    };
  }

  return {
    ...shared,
    backgroundColor: accent,
    color: '#000',
    '&:hover': {
      backgroundColor: accent,
      opacity: 0.92,
    },
    '&:disabled': {
      backgroundColor: isMobile ? 'rgba(255,255,255,0.08)' : '#cccccc',
      color: isMobile ? 'rgba(148,163,184,0.55)' : '#666666',
    },
  };
}

export function getWalkInStepSectionHeaderSx(isMobile = isWalkInMobileLayout()) {
  return {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    justifyContent: 'space-between',
    alignItems: { xs: 'stretch', sm: 'center' },
    gap: { xs: 1.25, sm: 2 },
    mb: { xs: 2, sm: 3 },
  };
}

export function getWalkInStepTitleSx(isMobile = isWalkInMobileLayout()) {
  return {
    fontWeight: 600,
    fontSize: { xs: '1rem', sm: '1.5rem' },
    color: isMobile ? '#f8fafc' : 'inherit',
    display: { xs: 'none', sm: 'block' },
  };
}

export function getWalkInNightsSelectorSx(isMobile = isWalkInMobileLayout()) {
  if (isMobile) {
    return {
      ...mobileAppGlassCard,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: 1,
      p: 1.25,
      width: '100%',
    };
  }
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    p: 2,
    borderRadius: 2,
    border: `1px solid rgba(255,255,255,0.08)`,
    backgroundColor: 'rgba(255,255,255,0.03)',
  };
}

export function getWalkInNightsControlRowSx() {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
    width: '100%',
  };
}

export function getWalkInHeadingActionsSx(isMobile = isWalkInMobileLayout()) {
  return {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    alignItems: { xs: 'stretch', sm: 'center' },
    gap: 1,
    flexWrap: 'wrap',
    justifyContent: { xs: 'stretch', sm: 'flex-end' },
    flexShrink: 0,
    width: { xs: '100%', sm: 'auto' },
    '& .MuiButton-root': {
      width: { xs: '100%', sm: 'auto' },
    },
  };
}

/** Room picker grid — matches dashboard density: 3 on phones, 6 on desktop. */
export function getWalkInRoomGridItemProps(isMobile = isWalkInMobileLayout()) {
  if (isMobile) {
    return { xs: 4, sm: 4, md: 3, lg: 2, xl: 2 };
  }
  return { xs: 6, sm: 4, md: 3, lg: 2, xl: 2 };
}

export function getWalkInRoomGridSpacing(isMobile = isWalkInMobileLayout()) {
  return isMobile ? 1 : 1.5;
}

function getWalkInRoomCardSurfaceSx(isDark, isMobile = isWalkInMobileLayout()) {
  if (isDark) {
    if (isMobile) {
      return {
        background: `linear-gradient(155deg, ${alpha('#ffffff', 0.07)} 0%, ${THEME_COLORS.SURFACE_ELEVATED_DARK} 52%, ${THEME_COLORS.SURFACE_HEADER_DARK} 100%)`,
        border: `1px solid ${alpha('#ffffff', 0.16)}`,
        boxShadow: `0 10px 24px rgba(0,0,0,0.42), inset 0 1px 0 ${alpha('#ffffff', 0.1)}`,
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
      };
    }
    return {
      background: `linear-gradient(165deg, ${alpha('#ffffff', 0.07)} 0%, ${THEME_COLORS.SURFACE_ELEVATED_DARK} 48%, ${THEME_COLORS.SURFACE_HEADER_DARK} 100%)`,
      border: `1px solid ${alpha('#ffffff', 0.14)}`,
      boxShadow: `0 10px 28px rgba(0,0,0,0.38), inset 0 1px 0 ${alpha('#ffffff', 0.08)}`,
    };
  }

  return isMobile
    ? {
        background: 'linear-gradient(155deg, rgba(255,255,255,0.98) 0%, #f8fafc 100%)',
        border: `1px solid ${alpha('#000', 0.1)}`,
        boxShadow: '0 8px 20px rgba(15,23,42,0.08), inset 0 1px 0 rgba(255,255,255,0.9)',
      }
    : {
        background: 'linear-gradient(165deg, #ffffff 0%, #f8fafc 100%)',
        border: `1px solid ${alpha('#000', 0.1)}`,
        boxShadow: '0 8px 20px rgba(15,23,42,0.08), inset 0 1px 0 rgba(255,255,255,0.9)',
      };
}

export function getWalkInRoomCardSx(isDark, isMobile = isWalkInMobileLayout(), options = {}) {
  const { isSelected = false, walkInHeld = false, roomTypeHasBlocks = false, blockedCount = 0 } = options;
  const accent = WALK_IN_ACCENT;
  const surface = getWalkInRoomCardSurfaceSx(isDark, isMobile);

  return {
    ...surface,
    borderRadius: isMobile ? '14px' : 2,
    height: '100%',
    cursor: walkInHeld ? 'not-allowed' : 'pointer',
    opacity: walkInHeld ? 0.74 : 1,
    border: isSelected
      ? `1px solid ${alpha(accent, isDark ? 0.55 : 0.5)}`
      : surface.border,
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: isSelected
      ? (isDark
        ? `0 16px 32px rgba(0,0,0,0.4), inset 0 0 0 1px ${alpha(accent, 0.28)}, inset 0 1px 0 ${alpha('#ffffff', 0.1)}`
        : `0 12px 24px rgba(15,23,42,0.12), inset 0 0 0 1px ${alpha(accent, 0.24)}`)
      : surface.boxShadow,
    '&::before': isSelected ? {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '3px',
      background: `linear-gradient(90deg, ${alpha(accent, 0.75)} 0%, ${alpha(accent, 0.25)} 70%, transparent 100%)`,
      zIndex: 1,
    } : roomTypeHasBlocks && blockedCount > 0 ? {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '3px',
      background: '#f44336',
      zIndex: 1,
    } : {},
    ...(!walkInHeld
      ? {
          '&:hover': {
            borderColor: isSelected ? alpha(accent, isDark ? 0.65 : 0.55) : alpha(accent, isDark ? 0.38 : 0.4),
            transform: isMobile ? 'none' : 'translateY(-4px)',
            boxShadow: isDark
              ? `0 14px 32px rgba(0,0,0,0.44), inset 0 0 0 1px ${alpha(accent, isSelected ? 0.32 : 0.16)}, inset 0 1px 0 ${alpha('#ffffff', 0.1)}`
              : `0 12px 24px rgba(15,23,42,0.12), inset 0 0 0 1px ${alpha(accent, 0.18)}`,
          },
        }
      : {}),
  };
}

export function getWalkInRoomCardContentSx(isMobile = isWalkInMobileLayout()) {
  return {
    p: isMobile ? 0.85 : 1.5,
    '&:last-child': { pb: isMobile ? 0.85 : 1.5 },
    position: 'relative',
  };
}

export function getWalkInRoomNumberSx(isMobile = isWalkInMobileLayout(), isDark = false) {
  return {
    fontWeight: 800,
    color: isDark ? '#f8fafc' : '#0f172a',
    fontSize: isMobile ? '0.88rem' : '1rem',
    mb: 0.25,
    lineHeight: 1.1,
    letterSpacing: '-0.02em',
  };
}

export function getWalkInRoomFloorSx(isMobile = isWalkInMobileLayout(), isDark = false) {
  return {
    color: isDark ? alpha('#e2e8f0', 0.78) : '#64748b',
    fontSize: isMobile ? '0.62rem' : '0.72rem',
    fontWeight: 600,
  };
}

export function getWalkInRoomStatusChipSx(colorKey, isMobile = isWalkInMobileLayout()) {
  const colorMap = {
    success: '#4caf50',
    warning: '#ff9800',
    info: '#2196f3',
    default: '#757575',
  };
  const color = colorMap[colorKey] || colorMap.default;

  return {
    height: isMobile ? 20 : 22,
    fontSize: isMobile ? '0.58rem' : '0.7rem',
    fontWeight: 700,
    backgroundColor: `${color}18`,
    color,
    border: `1.5px solid ${alpha(color, 0.72)}`,
    boxShadow: isMobile ? 'none' : `0 2px 8px ${alpha(color, 0.28)}`,
  };
}
